import React, { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import OverviewPage from './components/overviewSection/OverviewPage';
import DienstleisterDetail from './components/overviewSection/DienstleisterDetail';
import ContractPackagePage from './components/overviewSection/ContractPackagePage';
import AdminOverviewPage from './components/adminSection/AdminOverviewPage';
import OrganizationDetail from './components/adminSection/OrganizationDetail';
import ApiKeysPage from './components/settings/ApiKeysPage';
import Notification from './components/layouts/Notification';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRouteGuard from './components/common/AdminRouteGuard';
import QSRouteGuard from './components/common/QSRouteGuard';
import QSOverviewPage from './components/qsSection/QSOverviewPage';
import QSAnalysisDetail from './components/qsSection/QSAnalysisDetail';
import MainLayout from './components/layouts/MainLayout';

import { AuthProvider } from './context/AuthContextOIDC';
import { NotificationProvider } from './context/NotificationContext';
import { UploadProvider } from './context/UploadContext';
import { ThemeContextProvider, useThemeMode } from './context/ThemeContext';
import { useUserRole } from './hooks/useUserRole';
import Navbar from './components/Navbar';
import { getTheme } from './styles/theme';
import Box from '@mui/material/Box';

const AppContent: React.FC = () => {
  const { isAdminInForeignOrganization } = useUserRole();
  const { isDark } = useThemeMode();

  // Roter Rand-Style wenn Admin in fremder Organisation
  const containerBorderStyle = isAdminInForeignOrganization ? {
    border: '5px solid #dc2626',
    boxShadow: '0 0 25px rgba(220, 38, 38, 0.4)',
  } : {};

  return (
    <Box sx={{ 
      height: '100vh',
      ...containerBorderStyle
    }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        <Route path="/*" element={
          <ProtectedRoute>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              bgcolor: 'background.default'
            }}>
              <Navbar />
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Routes>
                  <Route path="/settings/api-keys" element={
                    <Box sx={{ 
                      height: 'calc(100vh - 60px)',
                      overflowY: 'auto',
                      bgcolor: 'background.default'
                    }}>
                      <ApiKeysPage />
                    </Box>
                  } />
                  
                  <Route path="/*" element={
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Routes>
                        <Route element={<MainLayout />}>
                          <Route path="/dienstleister" element={<Outlet />}>
                            <Route index element={<OverviewPage />} />
                            <Route path=":dienstleisterId" element={<DienstleisterDetail />} />
                            <Route path=":dienstleisterId/dienstleistung/:vertragId" element={<ContractPackagePage />} />
                          </Route>

                          <Route path="/admin" element={<AdminRouteGuard />}>
                              <Route index element={<AdminOverviewPage />} />
                              <Route path=":organizationId" element={<OrganizationDetail />} />
                          </Route>
                        </Route>

                        <Route path="/qs" element={<QSRouteGuard />}>
                            <Route index element={<QSOverviewPage />} />
                            <Route path="analysis/:analysisId" element={<QSAnalysisDetail />} />
                        </Route>

                        <Route path="/" element={<Navigate to="/dienstleister" replace />} />
                        <Route path="*" element={<Navigate to="/dienstleister" replace />} />
                      </Routes>
                    </Box>
                  } />
                </Routes>
              </Box>
            </Box>
          </ProtectedRoute>
        } />
      </Routes>
      <Notification />
    </Box>
  );
};

// Separates Komponente um Theme basierend auf Context zu setzen
const ThemedApp: React.FC = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <UploadProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </UploadProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeContextProvider>
      <ThemedApp />
    </ThemeContextProvider>
  );
};

export default App;
